import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import * as L from 'leaflet';

@Injectable({
  providedIn: 'root'
})
export class IconManagerService {
  // Icons for online/offline status
  private readonly onlineIcon = 'timeVerde.png';    // green for online users
  private readonly offlineIcon = 'timeVermelho.png'; // red for offline users
  
  // Keep original icons array for backward compatibility
  private readonly teamIcons = [
    'timeAmarelo.png',  // yellow - first company
    'timeLaranja.png',  // orange - second company
    'timeVerde.png',    // green - third company
    'timeVermelho.png'  // red - fourth company
  ];
  
  // Keep track of which company uses which icon
  private companyIconMap: Map<string, string> = new Map();
  private companyOrder: string[] = [];

  constructor() {}

  /**
   * Get icon URL for a specific company (maintaining backward compatibility)
   * @param companyName The name of the company
   * @returns Full URL to the icon image
   */
  getIconForCompany(companyName: string): string {
    if (!companyName) {
      return this.getDefaultIconUrl();
    }

    const companyNameLower = companyName.toLowerCase();
    
    // If we already have an icon assigned to this company, use it
    if (this.companyIconMap.has(companyNameLower)) {
      return this.getIconUrl(this.companyIconMap.get(companyNameLower)!);
    }
    
    // Otherwise, assign a new icon based on order
    const iconIndex = this.companyOrder.length % this.teamIcons.length;
    const iconName = this.teamIcons[iconIndex];
    
    // Store the company and its assigned icon
    this.companyOrder.push(companyNameLower);
    this.companyIconMap.set(companyNameLower, iconName);
    
    return this.getIconUrl(iconName);
  }
  
  /**
   * Get the L.icon object for Leaflet maps based on online status
   * @param isOnline Whether the user is online
   * @param size Icon size in pixels
   * @returns Leaflet icon object configured with the correct image
   */
  getLeafletIconByStatus(isOnline: boolean, size: number = 48): L.Icon {
    const iconName = isOnline ? this.onlineIcon : this.offlineIcon;
    const iconUrl = this.getIconUrl(iconName);
    
    return L.icon({
      iconUrl: iconUrl,
      iconSize: [size, size],
      iconAnchor: [size/2, size],
      popupAnchor: [0, -size]
    });
  }
  
  /**
   * Get the L.icon object for Leaflet maps (maintaining backward compatibility)
   * @param companyName The name of the company
   * @param size Icon size in pixels
   * @returns Leaflet icon object configured with the correct image
   */
  getLeafletIconForCompany(companyName: string, size: number = 48): L.Icon {
    const iconUrl = this.getIconForCompany(companyName);
    
    return L.icon({
      iconUrl: iconUrl,
      iconSize: [size, size],
      iconAnchor: [size/2, size],
      popupAnchor: [0, -size]
    });
  }
  
  /**
   * Get the full URL for an icon name
   * @param iconName Name of the icon file
   * @returns Full URL to the icon image
   */
  private getIconUrl(iconName: string): string {
    return `${environment.mediaUrl}/images/${iconName}`;
  }
  
  /**
   * Get the default icon URL
   * @returns URL for the default icon
   */
  private getDefaultIconUrl(): string {
    return this.getIconUrl('timeVermelho.png'); // Default to red icon
  }
}
